import { Fragment, useContext, useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { AuthContext } from "../../Auth/AuthProvider";
import { abpnClient } from "../../Services/AbpnClient";
import { LookupItem } from "../../Shared/Models/LookupItem";
import Resident, { ApplicationStatusModel } from "../../Residents/Models/Resident";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import ResidentPersonalInformation from "../../Residents/Components/ResidentPersonalInformation";
import AbpnSpinner from "../../Shared/Components/AbpnSpinner";
import { PostGraduateYearGrid } from "./PostGraduateYearGrid";
import { ClinicalSkillEvaluationsGrid } from "./ClinicalSkillEvaluationsGrid";
import { ProfileContext } from "../../Auth/ProfileProvider";
import { DocumentReviewStatus, PostGraduateYear } from "../Models/PostGraduateYear";
import { ClinicalSkill } from "../Models/ClinicalSkill";
import { TrainingDocument } from "../Models/TrainingDocument";
import TrainingAttestationGrid from "../Components/TrainingAttestationGrid";
import ProgramRemovalGrid from "../Components/ProgramRemovalGrid";
import { MedicalEducation } from "../../Residents/Models/MedicalEducation";
import ResidentInformation from "../../Residents/Models/ResidentInformation";
import { NotesGrid } from "./NotesGrid";
import { TrainingNote } from "../Models/TrainingNote";
import { Note } from "../../../../../Abpn.BoardCert.Web.Internal/Scripts/Common/Models/Note";
import ReturnToProgramGrid from "./ReturnToProgramGrid";
import { EligibilityRequirementsGrid } from "./EligibilityRequirementsGrid";
import { TrainingAttestation } from "../Models/TrainingAttestation";
import CompletedTrainingAttestations from "./CompletedTrainingAttestations";
import { Link } from "react-router-dom";

interface IProps {
    residentInfo: ResidentInformation;
    editResident: (resident: Resident) => void;
    updateResident: (resident: Resident) => void;
    onReturnedToProgram: (resident: Resident) => void;
    updateMedicalEducation: (medicalEducation: MedicalEducation) => void;
}

export default function ResidentTrainingDetails(props: IProps) {
    const [residentInfo, setResidentInfo] = useState<ResidentInformation>(props.residentInfo);
    const [medicalEducationLocationLookupList, setMedicalEducationLocationLookupList] = useState<Array<LookupItem>>([]);
    const [residentApprovedApplicationProgramTypIds, setResidentApprovedApplicationProgramTypIds] = useState<Array<number>>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isAlumni, setIsAlumni] = useState<boolean>(null);
    const [showFinalAttestationComponent, setFinalAttestationComponent] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    const authContext = useContext(AuthContext);
    const { user } = useContext(ProfileContext);
    let userProgramTypeId = user.trainingPrograms.find(_ => _.trainingProgramId === user.selectedTrainingProgramId).trainingProgramTypeId;

    useEffect(() => {
        setIsAlumni(residentInfo.resident.currentTrainingProgramId !== user.selectedTrainingProgramId);
        const loadData = async () => {
            setIsLoading(true);

            if (!medicalEducationLocationLookupList?.length || !props.residentInfo.medicalEducation) {
                await getFormLookupList();
                await getMedicalEducation();
                await getResidentProgramTypeIdsWithApprovedApplications();
            } 
            setFinalAttestationComponent(showFinalAttestationComponent())
            setIsLoading(false);
        }
        const getFormLookupList = async () => {
            let lookupList = await abpnClient.get<Array<LookupItem>>(`/api/training/GetMedicalEducationLocationLookupList`, authContext);
            setMedicalEducationLocationLookupList(lookupList);
        }
        const getMedicalEducation = async () => {
            let result = await abpnClient.get<MedicalEducation>(`/api/training/GetMedicalEducation?physicianId=${props.residentInfo.resident.physicianId}`, authContext);
            let updatedMedicalEducation = result ?? new MedicalEducation();
            let updated = deepCopy(props.residentInfo);
            updated.medicalEducation = updatedMedicalEducation;
            setResidentInfo(updated);
            props.updateMedicalEducation(updatedMedicalEducation);
        }

        const getResidentProgramTypeIdsWithApprovedApplications = async () => {
            let url = `/api/training/GetTrainingProgramTypeIdsWithApprovedApplications?physicianId=${props.residentInfo.resident.physicianId}&programTypeId=${userProgramTypeId}`
            let results = await abpnClient.get<Array<number>>(url, authContext);
            setResidentApprovedApplicationProgramTypIds(results);
        }

        const showFinalAttestationComponent = () => {
            let attestation = props.residentInfo.resident.trainingAttestations?.find(_ => _.trainingProgramId === user.selectedTrainingProgramId && _.trainingProgramTypeId == userProgramTypeId);

            if(attestation === undefined || attestation == null) return true;
            return !attestation.isAttestedComplete;
        }

        if(isLoading == false){
            loadData();
        }
    }, [props.residentInfo]);

    const saveResidentOnUpdates = async (updatedPostGraduateYears: Array<PostGraduateYear> = null, updatedClinicalSkills: Array<ClinicalSkill> = null, isOnFastTrack: boolean = null) => {
        setIsLoading(true);
        const newApplicationStatus = await getApplicationEligibilityStatus();

        updateResident((_: Resident) => {
            if (isOnFastTrack != null) _.isOnFastTrack = isOnFastTrack;

            if (updatedPostGraduateYears) _.postGraduateYears = updatedPostGraduateYears;

            if (updatedClinicalSkills) _.clinicalSkills = updatedClinicalSkills;

            _.applicationStatus = newApplicationStatus.applicationStatus;
            _.additionalFastTrackApplicationStatus = newApplicationStatus.additionalFastTrackApplicationStatus;
        });
        setIsLoading(false);
    }

    const saveResidentTrainingDocuments = async (updatedTrainingDocuments: Array<TrainingDocument>, pgy: PostGraduateYear) => {
        const newApplicationStatus = await getApplicationEligibilityStatus();
        updateResident((_: Resident) => {
            let updatedPgy = _.postGraduateYears.find(_ => _.trainingId === pgy.trainingId);
            updatedPgy.viewDocuments = pgy.viewDocuments;
            updatedPgy.trainingDocuments = updatedTrainingDocuments;
            updatedPgy.documentReviewStatus = updatedTrainingDocuments.length == 0 ? DocumentReviewStatus.NoDocuments : 
                updatedTrainingDocuments.length !== pgy.trainingDocuments.length && updatedTrainingDocuments.length > 0 ? DocumentReviewStatus.ToBeReviewed : 
                pgy.documentReviewStatus;
            _.applicationStatus = newApplicationStatus.applicationStatus;
            _.additionalFastTrackApplicationStatus = newApplicationStatus.additionalFastTrackApplicationStatus;
        });
    }

    const saveResidentAttestation = (attestations: Array<TrainingAttestation>) => {
        updateResident((_: Resident) => {
            _.trainingAttestations = attestations;
        });
    }

    const updateResident = (updateFunction: (n: Resident) => void): Resident => {
        let updated = deepCopy(residentInfo);
        updateFunction(updated.resident);
        props.updateResident(updated.resident);
        setResidentInfo(updated);
        console.log(updated.resident);
        return updated.resident;
    }

    const toggleShowDetails = async () => {

        updateResident((_: Resident) => {
            _.showDetails = !_.showDetails;
        })
    }

    const getApplicationEligibilityStatus = async () => {
        let url = `/api/training/GetApplicationEligibility?physicianId=${residentInfo.resident.physicianId}&programTypeId=${userProgramTypeId}`
        let statusModel = await abpnClient.get<ApplicationStatusModel>(url, authContext);
        return statusModel;
    }

    const getResidentStatus = () => {
        if (residentInfo.resident.currentTrainingProgramId) return 'Active';
        return `Alumni - Program ${residentInfo.resident.completedTrainingProgramIds.find(_ => _ === user.selectedTrainingProgramId) ? 'completed' : 'not completed'}`
    }

    const addNote = (newNote: Note) => {
        let newTrainingNote = new TrainingNote(residentInfo.resident.physicianId, "PreCert", newNote);

        const url = '/api/training/CreateTrainingNote';
        abpnClient.post<TrainingNote>(url, newTrainingNote, authContext).then((addedNote) => {
            updateResident((_: Resident) => {
                _.trainingNotes.push(addedNote);
            });
        });
    }

    const processTrainingNotes = () => {
        let trainingNotes = deepCopy(residentInfo.resident.trainingNotes);

        let legacyTrainingNotesContent = "";
        trainingNotes.forEach((_) => {
            if (_.note.createdBy == null) {
                legacyTrainingNotesContent += `${_.note.content} `
            }
            if (_.source != 'PreCert' && _.note.createdBy != null) {
                _.note.createdBy = "ABPN Credentials"
            }
        }, trainingNotes);

        if (legacyTrainingNotesContent != "") {
            let combinedLegacyNote = new TrainingNote(residentInfo.resident.physicianId, "Manual", { createdBy: "Program", createdDate: null, content: legacyTrainingNotesContent });
            trainingNotes.unshift(combinedLegacyNote)
        }

        return trainingNotes.filter(_ => _.note.createdBy != null).map(_ => _.note);
    }

    let processedNotes = processTrainingNotes();

    if (!user || !residentApprovedApplicationProgramTypIds) return <AbpnSpinner />

    return <Fragment>
        <Card className="border-pc-secondary-grey mt-4">
            <Card.Body>
                <Row>
                    <Col md={9}>
                        <Card className="border-pc-secondary-grey">
                            <Card.Body>
                                <div className="d-flex justify-content-between">
                                    <h5 className="fw-bolder ml-2 mt-1 text-primary">{residentInfo.resident.name.fullName}</h5>
                                    <div className="d-flex">
                                        <a className="me-2 link-primary" onClick={() => toggleShowDetails()}>{residentInfo.resident.showDetails ? "Hide" : "Show"} Details</a>
                                        <Link
                                            hidden={residentApprovedApplicationProgramTypIds?.length > 0 || !residentInfo.resident.showDetails || !residentInfo.resident.currentTrainingProgramId}
                                            to={`/Edit/${residentInfo.resident.physicianId}`}
                                        >Edit
                                        </Link>
                                    </div>
                                </div>
                                <span className="ml-2 mt-1 text-primary pc-font-size-smaller">{getResidentStatus()}</span>
                                {residentInfo.resident.showDetails &&
                                    <ResidentPersonalInformation medicalEducationLookup={medicalEducationLocationLookupList} residentInfo={residentInfo} />
                                }
                            </Card.Body>
                        </Card>
                        <Card className="border-pc-secondary-grey mt-4">
                            <Card.Body>
                                {isLoading ? <AbpnSpinner /> :
                                    <>
                                        <PostGraduateYearGrid
                                            physicianId={residentInfo.resident.physicianId}
                                            isOnFastTrack={residentInfo.resident.isOnFastTrack}
                                            pgyLookupList={props.residentInfo.resident.pgyLookupList}
                                            postGraduateYears={props.residentInfo.resident.postGraduateYears}
                                            programTypeIdsWithApprovedApplication={residentApprovedApplicationProgramTypIds}
                                            isReadonly={!residentInfo.resident.currentTrainingProgramId || isEditing}
                                            saveResident={
                                                (
                                                    postGraduateYears: Array<PostGraduateYear> = null,
                                                    clinicalSkills: Array<ClinicalSkill> = null,
                                                    isOnFastTrack: boolean = null
                                                ) =>
                                                    saveResidentOnUpdates(postGraduateYears, clinicalSkills, isOnFastTrack)
                                            }
                                            saveTrainingDocuments={
                                                (
                                                    trainingDocuments: Array<TrainingDocument>,
                                                    pgy: PostGraduateYear
                                                ) =>
                                                    saveResidentTrainingDocuments(trainingDocuments, pgy)}
                                            isAlumni={isAlumni}
                                            setIsEditing={(value: boolean) => setIsEditing(value)}
                                        />
                                        <hr />
                                        <ClinicalSkillEvaluationsGrid
                                            physicianId={residentInfo.resident.physicianId}
                                            clinicalSkills={props.residentInfo.resident.clinicalSkills}
                                            isReadonly={!residentInfo.resident.currentTrainingProgramId || isEditing}
                                            saveResident={
                                                (
                                                    postGraduateYears: Array<PostGraduateYear> = null,
                                                    clinicalSkills: Array<ClinicalSkill> = null,
                                                    isOnFastTrack: boolean = null
                                                ) =>
                                                    saveResidentOnUpdates(postGraduateYears, clinicalSkills, isOnFastTrack)
                                            }
                                            isAlumni={isAlumni}
                                            programTypeIdsWithApprovedApplication={residentApprovedApplicationProgramTypIds}
                                            setIsEditing={(value: boolean) => setIsEditing(value)}
                                        />
                                    </>
                                }
                            </Card.Body>
                        </Card>
                        {(residentInfo.resident.trainingAttestations.filter(_ => _.isAttestedComplete).length > 0) &&
                            <Card className="border-pc-secondary-grey mt-4">
                                <Card.Body>
                                    <CompletedTrainingAttestations trainingProgramId={user.selectedTrainingProgramId} residency={residentInfo.resident} />
                                </Card.Body>
                            </Card>
                        }

                        {(user.userRole.isProgramDirector && residentInfo.resident.currentTrainingProgramId === user.selectedTrainingProgramId) && showFinalAttestationComponent &&
                            <Card className="border-pc-secondary-grey mt-4">
                                <Card.Body>
                                    <TrainingAttestationGrid
                                        userTrainingProgram={user.trainingPrograms.find(_ => _.trainingProgramId === user.selectedTrainingProgramId)}
                                        resident={residentInfo.resident}
                                        saveResident={(attestations: Array<TrainingAttestation>) => saveResidentAttestation(attestations)}
                                    />
                                </Card.Body>
                            </Card>
                        }
                        {residentInfo.resident.currentTrainingProgramId === user.selectedTrainingProgramId &&
                            <Card className="border-pc-secondary-grey mt-4">
                                <Card.Body>
                                    <ProgramRemovalGrid residency={residentInfo.resident} userProgramTypeId={userProgramTypeId} />
                                </Card.Body>
                            </Card>
                        }
                        {isAlumni &&
                            <Card className="border-pc-secondary-grey mt-4">
                                <Card.Body>
                                    <ReturnToProgramGrid
                                        residency={residentInfo.resident} onReturnedToProgram={(resident: Resident) => props.onReturnedToProgram(resident)} />
                                </Card.Body>
                            </Card>
                        }
                    </Col>
                    <Col md={3}>
                        {!isAlumni && <EligibilityRequirementsGrid resident={residentInfo.resident} />}
                        <h5 className="fw-bolder ml-2 mt-4 mb-4 text-primary">Notes</h5>
                        <NotesGrid formId="trainingNotes" notes={processedNotes} isReadOnly={isAlumni} onSaving={(newNote) => addNote(newNote)}></NotesGrid>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    </Fragment>
}
